// src/components/Admin/Promotions/Discounts/DiscountAddEditForm.jsx
import React, { useState, useEffect, useRef } from "react";
import {
  X,
  Save,
  Tag,
  Percent,
  DollarSign,
  Calendar,
  Clock,
  ShoppingBag,
  Hash,
  Info,
  ChevronDown,
  ChevronUp,
  Settings,
  Search,
  Loader2,
  Gift,
  Package,
  TrendingUp,
  Plus,
  Trash2,
} from "lucide-react";
import { useMenuItems } from "../../../../hooks/useMenuItems";
import { categoryService } from "../../../../services/categoryService";
import { toast } from "react-hot-toast";

const DISCOUNT_TYPES = [
  { value: "PERCENTAGE", label: "Percentage", icon: Percent, description: "% off on bill or items" },
  { value: "FLAT", label: "Flat Amount", icon: DollarSign, description: "Fixed rupee amount off" },
  { value: "BOGO", label: "Buy 1 Get 1", icon: ShoppingBag, description: "Same item free" },
  { value: "BUY_X_GET_Y", label: "Buy X Get Y", icon: ShoppingBag, description: "Buy more, get free" },
  { value: "COMPLIMENTARY", label: "Complimentary", icon: Gift, description: "Buy X, get different item free" },
  { value: "BUNDLE", label: "Bundle Deal", icon: Package, description: "Combo at special price" },
  { value: "TIERED", label: "Tiered", icon: TrendingUp, description: "Bigger bill, bigger discount" },
];

const DISCOUNT_TYPE_HELP = {
  PERCENTAGE: {
    bg: "bg-teal-50",
    border: "border-teal-200",
    iconBg: "bg-teal-100",
    iconText: "text-teal-600",
    titleText: "text-teal-800",
    bodyText: "text-teal-700",
    tipText: "text-teal-600",
    exBorder: "border-teal-100",
    icon: Percent,
    title: "Percentage Discount",
    what: "Deducts a percentage of the bill total or specific item prices.",
    example: "20% off on bill — Customer's bill is ₹1,000 → They pay ₹800 (saves ₹200). You can set a max cap, e.g., max ₹150 off.",
    tip: "Use 'Max Discount' to cap high-value bills. Apply to specific items or categories to control margins.",
  },
  FLAT: {
    bg: "bg-green-50",
    border: "border-green-200",
    iconBg: "bg-green-100",
    iconText: "text-green-600",
    titleText: "text-green-800",
    bodyText: "text-green-700",
    tipText: "text-green-600",
    exBorder: "border-green-100",
    icon: DollarSign,
    title: "Flat Amount Discount",
    what: "Deducts a fixed rupee amount from the bill total or specific item prices.",
    example: "₹100 off on orders above ₹500 — Customer's bill is ₹750 → They pay ₹650 (saves ₹100).",
    tip: "Set a 'Min Bill Amount' in Conditions to prevent misuse on small orders.",
  },
  BOGO: {
    bg: "bg-purple-50",
    border: "border-purple-200",
    iconBg: "bg-purple-100",
    iconText: "text-purple-600",
    titleText: "text-purple-800",
    bodyText: "text-purple-700",
    tipText: "text-purple-600",
    exBorder: "border-purple-100",
    icon: ShoppingBag,
    title: "Buy One Get One Free (BOGO)",
    what: "Customer buys a set quantity of the same item and gets additional quantity of that same item free. Discount is applied on the cheapest qualifying item.",
    example: "Buy 1 Get 1 on Margherita Pizza (₹300) — Customer orders 2 Margherita Pizzas → Pays for 1, gets 1 free. Saves ₹300.",
    tip: "Works on the same item only. For 'buy pizza, get garlic bread free' use Complimentary type instead.",
  },
  BUY_X_GET_Y: {
    bg: "bg-indigo-50",
    border: "border-indigo-200",
    iconBg: "bg-indigo-100",
    iconText: "text-indigo-600",
    titleText: "text-indigo-800",
    bodyText: "text-indigo-700",
    tipText: "text-indigo-600",
    exBorder: "border-indigo-100",
    icon: ShoppingBag,
    title: "Buy X Get Y Free",
    what: "Customer buys X quantity and gets Y additional quantity of the same item free. More flexible version of BOGO.",
    example: "Buy 2 Get 1 Free on Mocktails (₹150 each) — Customer orders 3 Mocktails → Pays for 2 (₹300), gets 1 free. Saves ₹150.",
    tip: "The free items are always the cheapest ones from the qualifying set. Works on same item only.",
  },
  COMPLIMENTARY: {
    bg: "bg-emerald-50",
    border: "border-emerald-200",
    iconBg: "bg-emerald-100",
    iconText: "text-emerald-600",
    titleText: "text-emerald-800",
    bodyText: "text-emerald-700",
    tipText: "text-emerald-600",
    exBorder: "border-emerald-100",
    icon: Gift,
    title: "Complimentary (Cross-Item)",
    what: "Customer buys trigger items and gets a different item free. Unlike BOGO, the free item is a different menu item.",
    example: "Buy 2 Biryanis, get 1 Raita free — Customer orders 2 Chicken Biryani (₹350 each) → Gets 1 Raita (₹80) free. Pays ₹700 instead of ₹780.",
    tip: "Set trigger items in Conditions ('Applies To' → Items/Categories) and pick the free items in the 'Free Items' field above.",
  },
  BUNDLE: {
    bg: "bg-orange-50",
    border: "border-orange-200",
    iconBg: "bg-orange-100",
    iconText: "text-orange-600",
    titleText: "text-orange-800",
    bodyText: "text-orange-700",
    tipText: "text-orange-600",
    exBorder: "border-orange-100",
    icon: Package,
    title: "Bundle Deal (Combo)",
    what: "A fixed set of items sold together at a special combo price, lower than their individual total. All bundle items must be in the cart for the discount to apply.",
    example: "Lunch Combo: Burger (₹200) + Fries (₹100) + Coke (₹60) = Normally ₹360, Bundle price ₹299. Customer saves ₹61.",
    tip: "Select all bundle items in the Conditions section. 'Applies To' is auto-locked to 'Specific Items'. You need at least 2 items.",
  },
  TIERED: {
    bg: "bg-blue-50",
    border: "border-blue-200",
    iconBg: "bg-blue-100",
    iconText: "text-blue-600",
    titleText: "text-blue-800",
    bodyText: "text-blue-700",
    tipText: "text-blue-600",
    exBorder: "border-blue-100",
    icon: TrendingUp,
    title: "Tiered Discount (Slab-based)",
    what: "Progressive discount that increases with the bill amount. The highest qualifying slab is applied. Encourages customers to spend more.",
    example: "Bill ₹500+ → 5% off, Bill ₹1000+ → 10% off, Bill ₹2000+ → 15% off — Customer's bill is ₹1,200 → 10% slab applies → Saves ₹120.",
    tip: "Add slabs in ascending order of min amount. 'Applies To' is auto-locked to 'Bill Level'. Use 'Max Discount' to cap the amount.",
  },
};

const APPLICABLE_OPTIONS = [
  { value: "ALL", label: "All Items (Bill Level)" },
  { value: "CATEGORY", label: "Specific Categories" },
  { value: "ITEM", label: "Specific Items" },
  { value: "ORDER_TYPE", label: "Order Type (Bill Level)" },
];

const DAYS_OF_WEEK = [
  { value: "MONDAY", label: "Mon" },
  { value: "TUESDAY", label: "Tue" },
  { value: "WEDNESDAY", label: "Wed" },
  { value: "THURSDAY", label: "Thu" },
  { value: "FRIDAY", label: "Fri" },
  { value: "SATURDAY", label: "Sat" },
  { value: "SUNDAY", label: "Sun" },
];

const ORDER_TYPES = [
  { value: "DINE_IN", label: "Dine In" },
  { value: "TAKEAWAY", label: "Takeaway" },
  { value: "DELIVERY", label: "Delivery" },
  { value: "COUNTER", label: "Online / Counter" },
];


const ORDER_TYPE_BACKEND_MAP = {
  DINE_IN: "dine_in",
  TAKEAWAY: "takeaway",
  DELIVERY: "delivery",
  COUNTER: "online",
};

// ========== CATEGORY SEARCH SELECT ==========
const CategorySearchSelect = ({
  selectedCategories,
  onCategoriesChange,
  error,
  categories = [],
  loading = false,
}) => {
  const [searchText, setSearchText] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const filteredCategories = categories.filter((cat) => {
    const matchesSearch = searchText
      ? cat.name.toLowerCase().includes(searchText.toLowerCase())
      : true;
    const notSelected = !selectedCategories.some((selected) => selected.id === cat.id);
    return matchesSearch && notSelected;
  });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelectCategory = (category) => {
    onCategoriesChange([...selectedCategories, category]);
    setSearchText("");
    inputRef.current?.focus();
  };

  const handleRemoveCategory = (categoryId) => {
    onCategoriesChange(selectedCategories.filter((cat) => cat.id !== categoryId));
  };

  return (
    <div className="space-y-3">
      {selectedCategories.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedCategories.map((cat) => (
            <span
              key={cat.id}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-teal-50 text-teal-700 rounded-full text-sm border border-teal-200"
            >
              <span className="font-medium">{cat.name}</span>
              <button
                type="button"
                onClick={() => handleRemoveCategory(cat.id)}
                className="ml-1 p-0.5 hover:bg-teal-100 rounded-full transition-colors"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </span>
          ))}
        </div>
      )}

      <div ref={containerRef} style={{ position: "relative" }}>
        <div className="relative">
          <input
            ref={inputRef}
            type="text"
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
              setIsDropdownOpen(true);
            }}
            onFocus={() => setIsDropdownOpen(true)}
            placeholder={
              selectedCategories.length > 0
                ? "Add more categories..."
                : "Search categories..."
            }
            className={`w-full pl-9 pr-3 py-2 bg-white border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-100 focus:border-teal-500 ${
              error ? "border-red-300" : "border-gray-200"
            }`}
          />
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        </div>

        {isDropdownOpen && (
          <div
            className="absolute left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-64 overflow-y-auto"
            style={{
              zIndex: 9999,
              position: "absolute",
              top: "100%",
            }}
          >
            {loading ? (
              <div className="px-4 py-6 text-center">
                <Loader2 className="w-5 h-5 animate-spin text-teal-600 mx-auto mb-2" />
                <p className="text-sm text-gray-500">Loading categories...</p>
              </div>
            ) : filteredCategories.length > 0 ? (
              filteredCategories.map((cat) => (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => handleSelectCategory(cat)}
                  className="w-full px-3 py-2 text-left hover:bg-teal-50 flex items-center justify-between group transition-colors"
                >
                  <span className="text-sm text-gray-900">{cat.name}</span>
                </button>
              ))
            ) : (
              <div className="px-4 py-3 text-sm text-gray-500 text-center">
                {searchText
                  ? `No categories found for "${searchText}"`
                  : "No categories available"}
              </div>
            )}
          </div>
        )}
      </div>

      {error && <p className="text-xs text-red-500">{error}</p>}

      {selectedCategories.length === 0 && !error && (
        <p className="text-xs text-gray-500">
          Click the search box to see all categories, or type to filter
        </p>
      )}
    </div>
  );
};

// ========== ITEM SEARCH SELECT ==========
const ItemSearchSelect = ({
  selectedItems,
  onItemsChange,
  error,
  menuItems = [],
  loading = false,
}) => {
  const [searchText, setSearchText] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const filteredItems = menuItems.filter((item) => {
    const matchesSearch = searchText
      ? item.name.toLowerCase().includes(searchText.toLowerCase()) ||
        (item.category || "").toLowerCase().includes(searchText.toLowerCase())
      : true;
    const notSelected = !selectedItems.some((selected) => selected.id === item.id);
    return matchesSearch && notSelected;
  });

  const groupedItems = filteredItems.reduce((acc, item) => {
    const categoryName = item.category || "Uncategorized";
    if (!acc[categoryName]) acc[categoryName] = [];
    acc[categoryName].push(item);
    return acc;
  }, {});

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelectItem = (item) => {
    onItemsChange([...selectedItems, item]);
    setSearchText("");
    inputRef.current?.focus();
  };

  const handleRemoveItem = (itemId) => {
    onItemsChange(selectedItems.filter((item) => item.id !== itemId));
  };

  return (
    <div className="space-y-3">
      {selectedItems.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedItems.map((item) => (
            <span
              key={item.id}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-teal-50 text-teal-700 rounded-full text-sm border border-teal-200"
            >
              <span className="font-medium">{item.name}</span>
              <span className="text-teal-500 text-xs">
                ₹{item.basePrice || item.price || 0}
              </span>
              <button
                type="button"
                onClick={() => handleRemoveItem(item.id)}
                className="ml-1 p-0.5 hover:bg-teal-100 rounded-full transition-colors"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </span>
          ))}
        </div>
      )}

      <div ref={containerRef} style={{ position: "relative" }}>
        <div className="relative">
          <input
            ref={inputRef}
            type="text"
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
              setIsDropdownOpen(true);
            }}
            onFocus={() => setIsDropdownOpen(true)}
            placeholder={
              selectedItems.length > 0
                ? "Add more items..."
                : "Search menu items..."
            }
            className={`w-full pl-9 pr-3 py-2 bg-white border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-100 focus:border-teal-500 ${
              error ? "border-red-300" : "border-gray-200"
            }`}
          />
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        </div>

        {isDropdownOpen && (
          <div
            className="absolute left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-64 overflow-y-auto"
            style={{
              zIndex: 9999,
              position: "absolute",
              top: "100%",
            }}
          >
            {loading ? (
              <div className="px-4 py-6 text-center">
                <Loader2 className="w-5 h-5 animate-spin text-teal-600 mx-auto mb-2" />
                <p className="text-sm text-gray-500">Loading menu items...</p>
              </div>
            ) : filteredItems.length > 0 ? (
              Object.entries(groupedItems).map(([category, items]) => (
                <div key={category}>
                  <div className="px-3 py-1.5 bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wide sticky top-0 border-b border-gray-100">
                    {category}
                  </div>
                  {items.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => handleSelectItem(item)}
                      className="w-full px-3 py-2 text-left hover:bg-teal-50 flex items-center justify-between group transition-colors"
                    >
                      <span className="text-sm text-gray-900">{item.name}</span>
                      <span className="text-xs text-gray-500 group-hover:text-teal-600">
                        ₹{item.basePrice || item.price || 0}
                      </span>
                    </button>
                  ))}
                </div>
              ))
            ) : (
              <div className="px-4 py-3 text-sm text-gray-500 text-center">
                {searchText ? `No items found for "${searchText}"` : "No items available"}
              </div>
            )}
          </div>
        )}
      </div>

      {error && <p className="text-xs text-red-500">{error}</p>}

      {selectedItems.length === 0 && !error && (
        <p className="text-xs text-gray-500">
          Click the search box to see all items, or type to filter
        </p>
      )}
    </div>
  );
};

// ========== COLLAPSIBLE SECTION ==========
const Section = ({ title, icon: Icon, children, defaultOpen = false, badge = null }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border border-gray-200 rounded-lg" style={{ overflow: "visible" }}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors rounded-t-lg ${
          isOpen ? "border-b border-gray-200" : "rounded-b-lg"
        }`}
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-teal-100 flex items-center justify-center">
            <Icon className="w-4 h-4 text-teal-600" />
          </div>
          <span className="text-sm font-semibold text-gray-900">{title}</span>
          {badge && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-teal-100 text-teal-700">
              {badge}
            </span>
          )}
        </div>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-gray-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-400" />
        )}
      </button>
      {isOpen && (
        <div className="p-4 bg-white rounded-b-lg" style={{ overflow: "visible" }}>
          {children}
        </div>
      )}
    </div>
  );
};

/**
 * Helper: map API discount (backend shape) → form state shape
 * so editing works nicely.
 */
const mapApiToForm = (d = {}, categories = [], menuItems = []) => {
  // discountType: percentage | fixed | bogo | complimentary | bundle | tiered
  let type = "PERCENTAGE";
  if (d.discountType === "fixed") type = "FLAT";
  if (d.discountType === "bogo") {
    if (d.buyQuantity && d.getQuantity && d.buyQuantity !== 1) {
      type = "BUY_X_GET_Y";
    } else {
      type = "BOGO";
    }
  }
  if (d.discountType === "complimentary") type = "COMPLIMENTARY";
  if (d.discountType === "bundle") type = "BUNDLE";
  if (d.discountType === "tiered") type = "TIERED";

  // appliesTo: item | category | bill
  let applicableTo = "ALL";
  if (d.appliesTo === "item") applicableTo = "ITEM";
  if (d.appliesTo === "category") applicableTo = "CATEGORY";
  if (d.appliesTo === "bill") {
    applicableTo = (d.orderTypes && d.orderTypes.length > 0) ? "ORDER_TYPE" : "ALL";
  }

  const catMap = new Map(categories.map((c) => [c.id, c]));
  const itemMap = new Map(menuItems.map((m) => [m.id, m]));

  // Use API-provided menuItems/categories if available (has names), otherwise fall back to ID lookup
  let applicableCategories = [];
  if (d.categories && d.categories.length > 0) {
    // API now returns { id, name } objects - map to full category objects if available
    applicableCategories = d.categories.map((cat) => catMap.get(cat.id) || cat).filter(Boolean);
  } else if (d.idCategories && d.idCategories.length > 0) {
    // Fallback to ID lookup
    applicableCategories = d.idCategories.map((id) => catMap.get(id)).filter(Boolean);
  }

  let applicableItems = [];
  if (d.menuItems && d.menuItems.length > 0) {
    // API now returns { id, name } objects - map to full item objects if available
    applicableItems = d.menuItems.map((item) => itemMap.get(item.id) || item).filter(Boolean);
  } else if (d.idMenuItems && d.idMenuItems.length > 0) {
    // Fallback to ID lookup
    applicableItems = d.idMenuItems.map((id) => itemMap.get(id)).filter(Boolean);
  }

  const toUiOrderType = (ot) => {
    if (ot === "dine_in") return "DINE_IN";
    if (ot === "takeaway") return "TAKEAWAY";
    if (ot === "delivery") return "DELIVERY";
    if (ot === "online") return "COUNTER";
    return null;
  };

  return {
    name: d.name || "",
    code: d.code || "",
    description: d.description || "",
    type,
    value: d.discountValue ?? "",
    buyQuantity: d.buyQuantity ?? 2,
    getQuantity: d.getQuantity ?? 1,
    minOrderAmount: d.minBillAmount ?? "",
    maxDiscountAmount: d.maxDiscountAmount ?? "",
    applicableTo,
    applicableCategories,
    applicableItems,
    applicableOrderTypes: (d.orderTypes || [])
      .map(toUiOrderType)
      .filter(Boolean),
    startDate: d.validFrom || "",
    endDate: d.validUntil || "",
    startTime: d.startTime ? d.startTime.slice(0, 5) : "",
    endTime: d.endTime ? d.endTime.slice(0, 5) : "",
    daysOfWeek: (d.daysOfWeek || []).map((dw) => dw.toUpperCase()),
    usageLimit: d.usageLimit ?? "",
    usagePerCustomer: d.usagePerCustomer ?? "",
    isActive: d.isActive ?? true,
autoApply: d.isAutoApply ?? false,
stackable: d.isStackable ?? false,
    freeItems: (d.freeItems || []).map((item) => itemMap.get(item.id) || item).filter(Boolean),
    bundlePrice: d.bundlePrice ?? "",
    tierSlabs: (d.tierConfig && d.tierConfig.length > 0)
      ? d.tierConfig.map((s) => ({ minAmount: s.minAmount ?? "", value: s.value ?? "" }))
      : [{ minAmount: "", value: "" }],
  };
};

// put this in DiscountAddEditForm.jsx (or a shared util)
const mapFormToApi = (formData) => {
  const toNumberOrNull = (v) =>
    v === "" || v === null || v === undefined ? null : Number(v);

  // discountType mapping
  let discountType = "percentage";
  if (formData.type === "FLAT") discountType = "fixed";
  if (formData.type === "BOGO" || formData.type === "BUY_X_GET_Y")
    discountType = "bogo";
  if (formData.type === "COMPLIMENTARY") discountType = "complimentary";
  if (formData.type === "BUNDLE") discountType = "bundle";
  if (formData.type === "TIERED") discountType = "tiered";

  // appliesTo mapping (with type-based overrides)
  let appliesTo = "bill";
  if (formData.applicableTo === "CATEGORY") appliesTo = "category";
  if (formData.applicableTo === "ITEM") appliesTo = "item";
  // Force appliesTo for specific types
  if (discountType === "bundle") appliesTo = "item";
  if (discountType === "tiered") appliesTo = "bill";

  // id lists (use computed appliesTo which includes type-based overrides)
  const idMenuItems =
    appliesTo === "item"
      ? (formData.applicableItems || []).map((i) => i.id)
      : null;
  const idCategories =
    appliesTo === "category"
      ? (formData.applicableCategories || []).map((c) => c.id)
      : null;

  // daysOfWeek => lower-case strings
  const daysOfWeek =
    formData.daysOfWeek && formData.daysOfWeek.length
      ? formData.daysOfWeek.map((d) => d.toLowerCase())
      : [];

  // orderTypes mapping UI -> backend
  const ORDER_TYPE_BACKEND_MAP = {
    DINE_IN: "dine_in",
    TAKEAWAY: "takeaway",
    DELIVERY: "delivery",
    COUNTER: "online",
  };
  const orderTypes =
    (formData.applicableOrderTypes || []).map((ot) => ORDER_TYPE_BACKEND_MAP[ot]).filter(Boolean) || [];

  // discountValue: for BOGO/complimentary/bundle/tiered set 0
  const noValueTypes = ["bogo", "complimentary", "bundle", "tiered"];
  const discountValue = noValueTypes.includes(discountType)
    ? 0
    : toNumberOrNull(formData.value);

  const startTime =
    formData.startTime && formData.startTime.length === 5
      ? `${formData.startTime}:00`
      : null;
  const endTime =
    formData.endTime && formData.endTime.length === 5
      ? `${formData.endTime}:00`
      : null;

  const payload = {
    code: formData.code || null,
    name: (formData.name || "").trim(),
    description: formData.description || "",
    discountType,
    discountValue,

    appliesTo,
    idMenuItems,
    idCategories,

    validFrom: formData.startDate || null,
    validUntil: formData.endDate || null,
    startTime,
    endTime,

    daysOfWeek,
    orderTypes,

    minBillAmount: toNumberOrNull(formData.minOrderAmount) ?? 0,
    maxDiscountAmount: toNumberOrNull(formData.maxDiscountAmount),

    usageLimit: toNumberOrNull(formData.usageLimit),
    usagePerCustomer: toNumberOrNull(formData.usagePerCustomer),
    isAutoApply: Boolean(formData.autoApply),
    isStackable: Boolean(formData.stackable),
    isActive: Boolean(formData.isActive),
  };

  if (discountType === "bogo" || discountType === "complimentary") {
    payload.buyQuantity = toNumberOrNull(formData.buyQuantity) ?? null;
    payload.getQuantity = toNumberOrNull(formData.getQuantity) ?? null;
  }

  // Complimentary: free item IDs
  if (discountType === "complimentary") {
    payload.freeItemIds = (formData.freeItems || []).map((i) => i.id);
  }

  // Bundle: bundle price
  if (discountType === "bundle") {
    payload.bundlePrice = toNumberOrNull(formData.bundlePrice);
  }

  // Tiered: tier config slabs
  if (discountType === "tiered") {
    payload.tierConfig = (formData.tierSlabs || [])
      .filter((s) => s.minAmount !== "" && s.value !== "")
      .map((s) => ({
        minAmount: Number(s.minAmount),
        value: Number(s.value),
      }));
  }

  return payload;
};



export default function DiscountAddEditForm({ item, onSave, onClose }) {
  const { items: menuItems, loading: menuItemsLoading } = useMenuItems();

  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);

  useEffect(() => {
    const fetchCategories = async () => {
      setCategoriesLoading(true);
      try {
        const res = await categoryService.getAll();
        const data = Array.isArray(res)
          ? res
          : Array.isArray(res?.data)
          ? res.data
          : [];
        setCategories(data);
      } catch (error) {
        console.error("Failed to fetch categories:", error);
      } finally {
        setCategoriesLoading(false);
      }
    };
    fetchCategories();
  }, []);

  const [formData, setFormData] = useState({
    name: "",
    code: "",
    description: "",
    type: "PERCENTAGE",
    value: "",
    buyQuantity: 2,
    getQuantity: 1,
    minOrderAmount: "",
    maxDiscountAmount: "",
    applicableTo: "ALL",
    applicableCategories: [],
    applicableItems: [],
    applicableOrderTypes: [],
    startDate: "",
    endDate: "",
    startTime: "",
    endTime: "",
    daysOfWeek: [],
    usageLimit: "",
    usagePerCustomer: "",
    isActive: true,
    autoApply: false,
    stackable: false,
    freeItems: [],
    bundlePrice: "",
    tierSlabs: [{ minAmount: "", value: "" }],
  });

  const [errors, setErrors] = useState({});

  // When editing, map API discount → form state
  useEffect(() => {
    if (item) {
      setFormData((prev) => ({
        ...prev,
        ...mapApiToForm(item, categories, menuItems),
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item, categories, menuItems]);

  const handleChange = (field, value) => {
    setFormData((prev) => {
      const updated = { ...prev, [field]: value };
      // Auto-force applicableTo when type changes
      if (field === "type") {
        if (value === "BUNDLE") {
          updated.applicableTo = "ITEM";
        } else if (value === "TIERED") {
          updated.applicableTo = "ALL";
        }
      }
      return updated;
    });
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const handleDayToggle = (day) => {
    setFormData((prev) => ({
      ...prev,
      daysOfWeek: prev.daysOfWeek.includes(day)
        ? prev.daysOfWeek.filter((d) => d !== day)
        : [...prev.daysOfWeek, day],
    }));
  };

  const handleOrderTypeToggle = (orderType) => {
    setFormData((prev) => ({
      ...prev,
      applicableOrderTypes: prev.applicableOrderTypes.includes(orderType)
        ? prev.applicableOrderTypes.filter((t) => t !== orderType)
        : [...prev.applicableOrderTypes, orderType],
    }));
  };

  // ---------- VALIDATION ----------
 const validate = () => {
  const newErrors = {};

  // name
  if (!formData.name.trim()) {
    newErrors.name = "Discount name is required";
  } else if (formData.name.trim().length > 200) {
    newErrors.name = "Name cannot exceed 200 characters";
  }


  // promo code
  if (!formData.code.trim()) {
    newErrors.code = "Promo code is required";
  } else if (formData.code.trim().length > 50) {
    newErrors.code = "Promo code cannot exceed 50 characters";
  }

  const isBogoType =
    formData.type === "BOGO" || formData.type === "BUY_X_GET_Y";
  const noValueType =
    isBogoType || formData.type === "COMPLIMENTARY" || formData.type === "BUNDLE" || formData.type === "TIERED";
  if (!noValueType) {
    if (formData.value === "" || formData.value === null) {
      newErrors.value = "Discount value is required";
    } else {
      const valueNum = Number(formData.value);
      if (Number.isNaN(valueNum) || valueNum <= 0) {
        newErrors.value = "Discount value must be greater than 0";
      } else if (
        formData.type === "PERCENTAGE" &&
        (valueNum <= 0 || valueNum > 100)
      ) {
        newErrors.value = "Percentage must be between 0 and 100";
      }
    }
  }

  if (isBogoType || formData.type === "COMPLIMENTARY") {
    if (!formData.buyQuantity || Number(formData.buyQuantity) <= 0) {
      newErrors.buyQuantity = "Buy quantity must be greater than 0";
    }
    if (!formData.getQuantity || Number(formData.getQuantity) <= 0) {
      newErrors.getQuantity = "Get quantity must be greater than 0";
    }
  }

  // COMPLIMENTARY: require free items
  if (formData.type === "COMPLIMENTARY" && (!formData.freeItems || formData.freeItems.length === 0)) {
    newErrors.freeItems = "Select at least one free item";
  }

  // BUNDLE: require bundlePrice > 0 and at least 2 items
  if (formData.type === "BUNDLE") {
    if (!formData.bundlePrice || Number(formData.bundlePrice) <= 0) {
      newErrors.bundlePrice = "Bundle price must be greater than 0";
    }
    if (!formData.applicableItems || formData.applicableItems.length < 2) {
      newErrors.applicableItems = "Select at least 2 items for a bundle deal";
    }
  }

  // TIERED: require at least 1 valid slab
  if (formData.type === "TIERED") {
    const validSlabs = (formData.tierSlabs || []).filter(
      (s) => s.minAmount !== "" && s.value !== "" && Number(s.minAmount) > 0 && Number(s.value) > 0
    );
    if (validSlabs.length === 0) {
      newErrors.tierSlabs = "At least one valid tier slab is required";
    }
  }

  if (
    formData.applicableTo === "CATEGORY" &&
    formData.applicableCategories.length === 0
  ) {
    newErrors.applicableCategories = "Select at least one category";
  }

  if (
    formData.applicableTo === "ITEM" &&
    formData.applicableItems.length === 0
  ) {
    newErrors.applicableItems = "Select at least one item";
  }

  if (
    formData.applicableTo === "ORDER_TYPE" &&
    formData.applicableOrderTypes.length === 0
  ) {
    newErrors.applicableOrderTypes = "Select at least one order type";
  }


  if (formData.startTime && !formData.endTime) {
    newErrors.endTime = "End time is required when start time is set";
  }
  if (formData.endTime && !formData.startTime) {
    newErrors.startTime = "Start time is required when end time is set";
  }


  if (formData.usageLimit !== "" && Number(formData.usageLimit) <= 0) {
    newErrors.usageLimit = "Usage limit must be greater than 0";
  }
  if (
    formData.usagePerCustomer !== "" &&
    Number(formData.usagePerCustomer) <= 0
  ) {
    newErrors.usagePerCustomer =
      "Per customer limit must be greater than 0";
  }


  const startDateVal = formData.startDate ? formData.startDate.trim() : "";
  const endDateVal = formData.endDate ? formData.endDate.trim() : "";
  const startTimeVal = formData.startTime ? formData.startTime.trim() : "";
  const endTimeVal = formData.endTime ? formData.endTime.trim() : "";

  if (startDateVal && endDateVal) {
    const s = new Date(startDateVal);
    const e = new Date(endDateVal);
    if (isNaN(s.getTime()) || isNaN(e.getTime())) {

      newErrors.startDate = "Invalid start date";
      newErrors.endDate = "Invalid end date";
    } else {
      if (s > e) {
        newErrors.startDate = "Start date cannot be after end date";
        newErrors.endDate = "End date must be same or after start date";
      } else if (s.getTime() === e.getTime() && startTimeVal && endTimeVal) {

        if (startTimeVal > endTimeVal) {
          newErrors.startTime =
            "Start time cannot be after end time for the same day";
          newErrors.endTime =
            "End time must be same or after start time for the same day";
        }
      }
    }
  }

  setErrors(newErrors);
  return Object.keys(newErrors).length === 0;
};

  

const [isSubmitting, setIsSubmitting] = useState(false);

const handleSubmit = async (e) => {
  e.preventDefault();
  if (!validate()) return;

  const apiPayload = mapFormToApi(formData);
 console.log("API PAYLOAD:", apiPayload);
  try {
    setIsSubmitting(true);


    const result = await onSave?.(apiPayload);

    toast.success(item ? "Discount updated" : "Discount created");
    onClose?.();

    return result;
  } catch (error) {
    console.error("Save discount failed (form)", error);

 
    const serverMessage = error?.response?.data?.message || error?.message || "Failed to save discount";

   
    if (typeof serverMessage === "string" && serverMessage.toLowerCase().includes("code")) {
      setErrors((prev) => ({ ...prev, code: serverMessage }));
    }

    const fieldErrors = error?.response?.data?.errors;
    if (fieldErrors && typeof fieldErrors === "object") {
      setErrors((prev) => ({ ...prev, ...fieldErrors }));
    }

    toast.error(serverMessage);
    throw error; 
  } finally {
    setIsSubmitting(false);
  }
};


  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-teal-600 to-teal-700 px-6 py-4 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-lg">
              <Tag className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">
                {item ? "Edit Discount" : "Create New Discount"}
              </h2>
              <p className="text-xs text-teal-100">
                {item
                  ? "Update discount details"
                  : "Set up a new promotional discount"}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            type="button"
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-4">
            {/* BASIC INFO */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-teal-100 flex items-center justify-center">
                  <Tag className="w-4 h-4 text-teal-600" />
                </div>
                <h3 className="text-sm font-semibold text-gray-900">
                  Basic Information
                </h3>
                <span className="text-xs text-red-500">* Required</span>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    Discount Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleChange("name", e.target.value)}
                    placeholder="e.g., Weekday Lunch Special"
                    className={`w-full px-3 py-2 bg-white border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-100 focus:border-teal-500 ${
                      errors.name ? "border-red-300" : "border-gray-200"
                    }`}
                  />
                  {errors.name && (
                    <p className="text-xs text-red-500 mt-1">
                      {errors.name}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    Promo Code <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={formData.code}
                      onChange={(e) =>
                        handleChange("code", e.target.value.toUpperCase())
                      }
                      placeholder="e.g., WEEKDAY20"
                      className={`w-full pl-8 pr-3 py-2 bg-white border rounded-lg text-sm font-mono uppercase focus:outline-none focus:ring-2 focus:ring-teal-100 focus:border-teal-500 ${
                        errors.code ? "border-red-300" : "border-gray-200"
                      }`}
                    />
                    <Hash className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  </div>
                  {errors.code && (
                    <p className="text-xs text-red-500 mt-1">
                      {errors.code}
                    </p>
                  )}
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  Description <span className="text-gray-400">(Optional)</span>
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleChange("description", e.target.value)}
                  rows={2}
                  placeholder="Brief description of the offer"
                  className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-100 focus:border-teal-500 resize-none"
                />
              </div>

              {/* Active toggle */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    Active Status
                  </p>
                  <p className="text-xs text-gray-500">
                    Enable this discount immediately
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => handleChange("isActive", !formData.isActive)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    formData.isActive ? "bg-teal-600" : "bg-gray-300"
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                      formData.isActive ? "translate-x-6" : "translate-x-1"
                    }`}
                  />
                </button>
              </div>
            </div>

            {/* DISCOUNT VALUE */}
            <div className="space-y-4 pt-4 border-t border-gray-200">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-teal-100 flex items-center justify-center">
                  <Percent className="w-4 h-4 text-teal-600" />
                </div>
                <h3 className="text-sm font-semibold text-gray-900">
                  Discount Value
                </h3>
                <span className="text-xs text-red-500">* Required</span>
              </div>

              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {DISCOUNT_TYPES.map((type) => {
                  const Icon = type.icon;
                  return (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => handleChange("type", type.value)}
                      className={`p-3 rounded-lg border-2 text-center transition-all ${
                        formData.type === type.value
                          ? "border-teal-500 bg-teal-50"
                          : "border-gray-200 hover:border-gray-300 bg-white"
                      }`}
                    >
                      <Icon
                        className={`w-5 h-5 mx-auto mb-1 ${
                          formData.type === type.value
                            ? "text-teal-600"
                            : "text-gray-400"
                        }`}
                      />
                      <p
                        className={`text-xs font-medium ${
                          formData.type === type.value
                            ? "text-teal-700"
                            : "text-gray-700"
                        }`}
                      >
                        {type.label}
                      </p>
                    </button>
                  );
                })}
              </div>

              {/* Type Help Info */}
              {DISCOUNT_TYPE_HELP[formData.type] && (() => {
                const h = DISCOUNT_TYPE_HELP[formData.type];
                const HelpIcon = h.icon;
                return (
                  <div className={`p-3 rounded-lg border ${h.bg} ${h.border}`}>
                    <div className="flex items-start gap-2.5">
                      <div className={`w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 mt-0.5 ${h.iconBg}`}>
                        <HelpIcon className={`w-3.5 h-3.5 ${h.iconText}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-semibold ${h.titleText}`}>{h.title}</p>
                        <p className={`text-xs mt-1 ${h.bodyText}`}>{h.what}</p>
                        <div className={`mt-2 p-2 bg-white/60 rounded-md border ${h.exBorder}`}>
                          <p className={`text-xs font-medium ${h.titleText}`}>Example:</p>
                          <p className={`text-xs mt-0.5 ${h.bodyText}`}>{h.example}</p>
                        </div>
                        <p className={`text-xs mt-1.5 italic ${h.tipText}`}>{h.tip}</p>
                      </div>
                    </div>
                  </div>
                );
              })()}

              <div className="grid grid-cols-2 gap-4">
                {formData.type === "PERCENTAGE" && (
                  <>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1.5">
                        Percentage Off <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <input
                          type="number"
                          min="0"
                          max="100"
                          value={formData.value}
                          onChange={(e) =>
                            handleChange("value", e.target.value)
                          }
                          placeholder="20"
                          className={`w-full pl-8 pr-10 py-2 bg-white border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-100 focus:border-teal-500 ${
                            errors.value ? "border-red-300" : "border-gray-200"
                          }`}
                        />
                        <Percent className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">
                          %
                        </span>
                      </div>
                      {errors.value && (
                        <p className="text-xs text-red-500 mt-1">
                          {errors.value}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1.5">
                        Max Discount{" "}
                        <span className="text-gray-400">(Optional)</span>
                      </label>
                      <div className="relative">
                        <input
                          type="number"
                          min="0"
                          value={formData.maxDiscountAmount}
                          onChange={(e) =>
                            handleChange("maxDiscountAmount", e.target.value)
                          }
                          placeholder="150"
                          className="w-full pl-8 pr-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-100 focus:border-teal-500"
                        />
                        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm">
                          ₹
                        </span>
                      </div>
                    </div>
                  </>
                )}

                {formData.type === "FLAT" && (
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      Discount Amount <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        min="0"
                        value={formData.value}
                        onChange={(e) =>
                          handleChange("value", e.target.value)
                        }
                        placeholder="50"
                        className={`w-full pl-8 pr-3 py-2 bg-white border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-100 focus:border-teal-500 ${
                          errors.value ? "border-red-300" : "border-gray-200"
                        }`}
                      />
                      <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm">
                        ₹
                      </span>
                    </div>
                    {errors.value && (
                      <p className="text-xs text-red-500 mt-1">
                        {errors.value}
                      </p>
                    )}
                  </div>
                )}

                {formData.type === "BUY_X_GET_Y" && (
                  <>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1.5">
                        Buy Quantity <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={formData.buyQuantity}
                        onChange={(e) =>
                          handleChange(
                            "buyQuantity",
                            e.target.value ? parseInt(e.target.value, 10) : ""
                          )
                        }
                        className={`w-full px-3 py-2 bg-white border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-100 focus:border-teal-500 ${
                          errors.buyQuantity
                            ? "border-red-300"
                            : "border-gray-200"
                        }`}
                      />
                      {errors.buyQuantity && (
                        <p className="text-xs text-red-500 mt-1">
                          {errors.buyQuantity}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1.5">
                        Get Free Quantity{" "}
                        <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={formData.getQuantity}
                        onChange={(e) =>
                          handleChange(
                            "getQuantity",
                            e.target.value ? parseInt(e.target.value, 10) : ""
                          )
                        }
                        className={`w-full px-3 py-2 bg-white border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-100 focus:border-teal-500 ${
                          errors.getQuantity
                            ? "border-red-300"
                            : "border-gray-200"
                        }`}
                      />
                      {errors.getQuantity && (
                        <p className="text-xs text-red-500 mt-1">
                          {errors.getQuantity}
                        </p>
                      )}
                    </div>
                  </>
                )}

                {formData.type === "BOGO" && (
                  <div className="col-span-2 space-y-3">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1.5">
                          Buy Quantity <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="number"
                          min="1"
                          value={formData.buyQuantity}
                          onChange={(e) =>
                            handleChange(
                              "buyQuantity",
                              e.target.value
                                ? parseInt(e.target.value, 10)
                                : ""
                            )
                          }
                          className={`w-full px-3 py-2 bg-white border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-100 focus:border-teal-500 ${
                            errors.buyQuantity
                              ? "border-red-300"
                              : "border-gray-200"
                          }`}
                        />
                        {errors.buyQuantity && (
                          <p className="text-xs text-red-500 mt-1">
                            {errors.buyQuantity}
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1.5">
                          Get Free Quantity{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="number"
                          min="1"
                          value={formData.getQuantity}
                          onChange={(e) =>
                            handleChange(
                              "getQuantity",
                              e.target.value
                                ? parseInt(e.target.value, 10)
                                : ""
                            )
                          }
                          className={`w-full px-3 py-2 bg-white border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-100 focus:border-teal-500 ${
                            errors.getQuantity
                              ? "border-red-300"
                              : "border-gray-200"
                          }`}
                        />
                        {errors.getQuantity && (
                          <p className="text-xs text-red-500 mt-1">
                            {errors.getQuantity}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* COMPLIMENTARY */}
                {formData.type === "COMPLIMENTARY" && (
                  <div className="col-span-2 space-y-3">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1.5">
                          Buy Quantity <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="number"
                          min="1"
                          value={formData.buyQuantity}
                          onChange={(e) =>
                            handleChange("buyQuantity", e.target.value ? parseInt(e.target.value, 10) : "")
                          }
                          className={`w-full px-3 py-2 bg-white border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-100 focus:border-teal-500 ${
                            errors.buyQuantity ? "border-red-300" : "border-gray-200"
                          }`}
                        />
                        {errors.buyQuantity && <p className="text-xs text-red-500 mt-1">{errors.buyQuantity}</p>}
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1.5">
                          Get Free Quantity <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="number"
                          min="1"
                          value={formData.getQuantity}
                          onChange={(e) =>
                            handleChange("getQuantity", e.target.value ? parseInt(e.target.value, 10) : "")
                          }
                          className={`w-full px-3 py-2 bg-white border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-100 focus:border-teal-500 ${
                            errors.getQuantity ? "border-red-300" : "border-gray-200"
                          }`}
                        />
                        {errors.getQuantity && <p className="text-xs text-red-500 mt-1">{errors.getQuantity}</p>}
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-2">
                        Free Items <span className="text-red-500">*</span>
                      </label>
                      <ItemSearchSelect
                        selectedItems={formData.freeItems}
                        onItemsChange={(items) => handleChange("freeItems", items)}
                        error={errors.freeItems}
                        menuItems={menuItems}
                        loading={menuItemsLoading}
                      />
                    </div>
                  </div>
                )}

                {/* BUNDLE */}
                {formData.type === "BUNDLE" && (
                  <div className="col-span-2 space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1.5">
                        Bundle Price <span className="text-red-500">*</span>
                      </label>
                      <div className="relative max-w-xs">
                        <input
                          type="number"
                          min="0"
                          value={formData.bundlePrice}
                          onChange={(e) => handleChange("bundlePrice", e.target.value)}
                          placeholder="299"
                          className={`w-full pl-8 pr-3 py-2 bg-white border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-100 focus:border-teal-500 ${
                            errors.bundlePrice ? "border-red-300" : "border-gray-200"
                          }`}
                        />
                        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm">₹</span>
                      </div>
                      {errors.bundlePrice && <p className="text-xs text-red-500 mt-1">{errors.bundlePrice}</p>}
                    </div>

                    <p className="text-xs text-gray-500">
                      Select the bundle items in the Conditions section below (Applies To → Specific Items).
                    </p>
                  </div>
                )}

                {/* TIERED */}
                {formData.type === "TIERED" && (
                  <div className="col-span-2 space-y-3">
                    <div className="space-y-2">
                      {formData.tierSlabs.map((slab, idx) => (
                        <div key={idx} className="flex items-center gap-3">
                          <div className="flex-1">
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Min Amount (₹)
                            </label>
                            <div className="relative">
                              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm">₹</span>
                              <input
                                type="number"
                                min="0"
                                value={slab.minAmount}
                                onChange={(e) => {
                                  const updated = [...formData.tierSlabs];
                                  updated[idx] = { ...updated[idx], minAmount: e.target.value };
                                  handleChange("tierSlabs", updated);
                                }}
                                placeholder="500"
                                className="w-full pl-7 pr-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-100 focus:border-teal-500"
                              />
                            </div>
                          </div>
                          <div className="flex-1">
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Discount (%)
                            </label>
                            <div className="relative">
                              <input
                                type="number"
                                min="0"
                                max="100"
                                value={slab.value}
                                onChange={(e) => {
                                  const updated = [...formData.tierSlabs];
                                  updated[idx] = { ...updated[idx], value: e.target.value };
                                  handleChange("tierSlabs", updated);
                                }}
                                placeholder="10"
                                className="w-full pl-3 pr-7 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-100 focus:border-teal-500"
                              />
                              <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm">%</span>
                            </div>
                          </div>
                          {formData.tierSlabs.length > 1 && (
                            <button
                              type="button"
                              onClick={() => {
                                const updated = formData.tierSlabs.filter((_, i) => i !== idx);
                                handleChange("tierSlabs", updated);
                              }}
                              className="mt-5 p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                            >
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      ))}

                      <button
                        type="button"
                        onClick={() =>
                          handleChange("tierSlabs", [
                            ...formData.tierSlabs,
                            { minAmount: "", value: "" },
                          ])
                        }
                        className="flex items-center gap-1.5 text-xs font-medium text-teal-600 hover:text-teal-700 px-2 py-1.5 rounded-lg hover:bg-teal-50 transition"
                      >
                        <Plus size={14} />
                        Add Slab
                      </button>
                    </div>
                    {errors.tierSlabs && <p className="text-xs text-red-500">{errors.tierSlabs}</p>}
                  </div>
                )}
              </div>
            </div>

            {/* CONDITIONS */}
            <Section
              title="Conditions"
              icon={ShoppingBag}
              defaultOpen={false}
              badge={
                formData.minOrderAmount ||
                formData.applicableCategories.length > 0 ||
                formData.applicableItems.length > 0 ||
                formData.applicableOrderTypes.length > 0
                  ? "Configured"
                  : null
              }
            >
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    Minimum Bill Amount
                  </label>
                  <div className="relative max-w-xs">
                    <input
                      type="number"
                      min="0"
                      value={formData.minOrderAmount}
                      onChange={(e) =>
                        handleChange("minOrderAmount", e.target.value)
                      }
                      placeholder="300"
                      className="w-full pl-8 pr-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-100 focus:border-teal-500"
                    />
                    <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm">
                      ₹
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">
                    Applies To
                    {formData.type === "BUNDLE" && (
                      <span className="ml-2 text-xs text-orange-600 font-normal">(locked to Items for Bundle)</span>
                    )}
                    {formData.type === "TIERED" && (
                      <span className="ml-2 text-xs text-blue-600 font-normal">(locked to Bill for Tiered)</span>
                    )}
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {APPLICABLE_OPTIONS.map((option) => {
                      const isLocked = formData.type === "BUNDLE" || formData.type === "TIERED";
                      return (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => !isLocked && handleChange("applicableTo", option.value)}
                          disabled={isLocked}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                            formData.applicableTo === option.value
                              ? "bg-teal-600 text-white"
                              : isLocked
                                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                          }`}
                        >
                          {option.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {formData.applicableTo === "CATEGORY" && (
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">
                      Select Categories <span className="text-red-500">*</span>
                    </label>
                    <CategorySearchSelect
                      selectedCategories={formData.applicableCategories}
                      onCategoriesChange={(cats) =>
                        handleChange("applicableCategories", cats)
                      }
                      error={errors.applicableCategories}
                      categories={categories}
                      loading={categoriesLoading}
                    />
                  </div>
                )}

                {formData.applicableTo === "ORDER_TYPE" && (
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">
                      Select Order Types <span className="text-red-500">*</span>
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {ORDER_TYPES.map((orderType) => (
                        <button
                          key={orderType.value}
                          type="button"
                          onClick={() =>
                            handleOrderTypeToggle(orderType.value)
                          }
                          className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                            formData.applicableOrderTypes.includes(
                              orderType.value
                            )
                              ? "bg-teal-600 text-white"
                              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                          }`}
                        >
                          {orderType.label}
                        </button>
                      ))}
                    </div>
                    {errors.applicableOrderTypes && (
                      <p className="text-xs text-red-500 mt-1">
                        {errors.applicableOrderTypes}
                      </p>
                    )}
                  </div>
                )}

                {formData.applicableTo === "ITEM" && (
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">
                      Select Menu Items <span className="text-red-500">*</span>
                    </label>
                    <ItemSearchSelect
                      selectedItems={formData.applicableItems}
                      onItemsChange={(items) =>
                        handleChange("applicableItems", items)
                      }
                      error={errors.applicableItems}
                      menuItems={menuItems}
                      loading={menuItemsLoading}
                    />
                  </div>
                )}
              </div>
            </Section>

            {/* VALIDITY */}
            <Section
              title="Validity Period"
              icon={Calendar}
              defaultOpen={false}
              badge={
                formData.startDate || formData.daysOfWeek.length > 0
                  ? "Configured"
                  : null
              }
            >
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      Start Date
                    </label>
                    <div className="relative">
                      <input
                        type="date"
                        value={formData.startDate}
                        onChange={(e) =>
                          handleChange("startDate", e.target.value)
                        }
                        className="w-full pl-8 pr-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-100 focus:border-teal-500"
                      />
                      <Calendar className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      End Date
                    </label>
                    <div className="relative">
                      <input
                        type="date"
                        value={formData.endDate}
                        onChange={(e) =>
                          handleChange("endDate", e.target.value)
                        }
                        className="w-full pl-8 pr-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-100 focus:border-teal-500"
                      />
                      <Calendar className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      Start Time
                    </label>
                    <div className="relative">
                      <input
                        type="time"
                        value={formData.startTime}
                        onChange={(e) =>
                          handleChange("startTime", e.target.value)
                        }
                        className={`w-full pl-8 pr-3 py-2 bg-white border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-100 focus:border-teal-500 ${
                          errors.startTime ? "border-red-300" : "border-gray-200"
                        }`}
                      />
                      <Clock className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    </div>
                    {errors.startTime && (
                      <p className="text-xs text-red-500 mt-1">
                        {errors.startTime}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      End Time
                    </label>
                    <div className="relative">
                      <input
                        type="time"
                        value={formData.endTime}
                        onChange={(e) =>
                          handleChange("endTime", e.target.value)
                        }
                        className={`w-full pl-8 pr-3 py-2 bg-white border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-100 focus:border-teal-500 ${
                          errors.endTime ? "border-red-300" : "border-gray-200"
                        }`}
                      />
                      <Clock className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    </div>
                    {errors.endTime && (
                      <p className="text-xs text-red-500 mt-1">
                        {errors.endTime}
                      </p>
                    )}
                  </div>
                </div>
                <p className="text-xs text-gray-500">
                  Set daily time window (e.g., lunch 12–15) or leave blank for
                  full day.
                </p>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">
                    Active Days
                  </label>
                  <div className="flex gap-2">
                    {DAYS_OF_WEEK.map((day) => (
                      <button
                        key={day.value}
                        type="button"
                        onClick={() => handleDayToggle(day.value)}
                        className={`w-10 h-10 rounded-lg text-xs font-medium transition-all ${
                          formData.daysOfWeek.includes(day.value)
                            ? "bg-teal-600 text-white"
                            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        }`}
                      >
                        {day.label}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Leave all unselected to apply on all days.
                  </p>
                </div>
              </div>
            </Section>

            {/* USAGE LIMITS */}
            <Section
              title="Usage Limits & Settings"
              icon={Settings}
              defaultOpen={false}
              badge={
                formData.usageLimit || formData.autoApply ? "Configured" : null
              }
            >
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      Total Usage Limit
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={formData.usageLimit}
                      onChange={(e) =>
                        handleChange("usageLimit", e.target.value)
                      }
                      placeholder="Unlimited"
                      className={`w-full px-3 py-2 bg-white border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-100 focus:border-teal-500 ${
                        errors.usageLimit
                          ? "border-red-300"
                          : "border-gray-200"
                      }`}
                    />
                    {errors.usageLimit && (
                      <p className="text-xs text-red-500 mt-1">
                        {errors.usageLimit}
                      </p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      Max total uses (optional).
                    </p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      Per Customer Limit
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={formData.usagePerCustomer}
                      onChange={(e) =>
                        handleChange("usagePerCustomer", e.target.value)
                      }
                      placeholder="Unlimited"
                      className={`w-full px-3 py-2 bg-white border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-100 focus:border-teal-500 ${
                        errors.usagePerCustomer
                          ? "border-red-300"
                          : "border-gray-200"
                      }`}
                    />
                    {errors.usagePerCustomer && (
                      <p className="text-xs text-red-500 mt-1">
                        {errors.usagePerCustomer}
                      </p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      Max uses per customer (optional).
                    </p>
                  </div>
                </div>

                <div className="space-y-3 pt-3 border-t border-gray-200">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        Auto-Apply
                      </p>
                      <p className="text-xs text-gray-500">
                        Automatically apply when conditions are met.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        handleChange("autoApply", !formData.autoApply)
                      }
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        formData.autoApply ? "bg-teal-600" : "bg-gray-300"
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                          formData.autoApply ? "translate-x-6" : "translate-x-1"
                        }`}
                      />
                    </button>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        Stackable
                      </p>
                      <p className="text-xs text-gray-500">
                        Can be combined with other discounts.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        handleChange("stackable", !formData.stackable)
                      }
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        formData.stackable ? "bg-teal-600" : "bg-gray-300"
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                          formData.stackable ? "translate-x-6" : "translate-x-1"
                        }`}
                      />
                    </button>
                  </div>
                </div>
              </div>
            </Section>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 border-t border-gray-200 px-6 py-4 flex items-center justify-end gap-3 flex-shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 bg-white border border-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 hover:border-gray-300 transition-all"
            >
              Cancel
            </button>
            <button
  type="submit"
  disabled={isSubmitting}
  className="px-5 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-all flex items-center gap-2"
>
  <Save className="w-4 h-4" />
  {item ? "Update Discount" : "Create Discount"}
</button>
          </div>
        </form>
      </div>
    </div>
  );
}
